import { useTranslation } from "react-i18next";
import { cn } from "@tellescope/utils";
import { Label } from "@tellescope/ui";
import { FieldDescription } from "../../FieldDescription";
import { useFileFieldLogic, type FileFieldProps } from "./useFileFieldLogic";
import { FileFieldDropzone } from "./FileFieldDropzone";
import { FileFieldList } from "./FileFieldList";

export const FileField = ({
  title,
  description,
  htmlDescription,
  value = [],
  onChange,
  className,
  validFileTypes,
  multiple = false,
  maxSizeMB = 1024,
  maxFiles,
  enduserId,
  autoUploadFiles = false,
  hideFromPortal,
  pendingFiles,
  onPendingFilesChange,
  error: validationError,
  required,
}: FileFieldProps) => {
  const { t } = useTranslation("forms");
  const logic = useFileFieldLogic({
    value,
    onChange,
    validFileTypes,
    multiple,
    maxSizeMB,
    maxFiles,
    enduserId,
    autoUploadFiles,
    hideFromPortal,
    pendingFiles,
    onPendingFilesChange,
  });

  return (
    <div className={cn("space-y-2", className)}>
      {!!title && (
        <Label className="text-sm font-medium" required={required}>
          {title}
        </Label>
      )}
      <FieldDescription description={description} htmlDescription={htmlDescription} />

      <input
        ref={logic.fileInputRef}
        type="file"
        multiple={multiple}
        accept={logic.normalizedFileTypes}
        onChange={logic.handleInputChange}
        className="hidden"
        aria-label={title || t("Choose files...")}
      />

      <FileFieldDropzone
        multiple={multiple}
        isDragging={logic.isDragging}
        hasValidationError={!!validationError}
        validFileTypes={validFileTypes}
        maxSizeMB={maxSizeMB}
        onClick={logic.handleClick}
        onDragEnter={logic.handleDragEnter}
        onDragLeave={logic.handleDragLeave}
        onDragOver={logic.handleDragOver}
        onDrop={logic.handleDrop}
      />

      {(logic.error || validationError) && <p className="text-sm text-destructive">{logic.error || validationError}</p>}

      {logic.showFileList && (
        <FileFieldList
          fileListCount={logic.fileListCount}
          uploadedFiles={logic.displayedUploadedFiles}
          inFlightFiles={logic.inFlightFiles}
          inFlightVariant={logic.inFlightVariant}
          erroredFiles={logic.erroredFiles}
          onRemoveUploaded={logic.handleRemoveUploadedFile}
          onRemoveInFlight={logic.handleRemovePendingFile}
          onRemoveErrored={logic.handleRemoveErroredFile}
          onRetryUpload={logic.handleRetryUpload}
        />
      )}
    </div>
  );
};
